import { normalizeProductCategories } from "./productCategories";

export const ARRAY_FILTER_KEYS = [
  "kinds",
  "types",
  "purposes",
  "manufacturers",
  "countries",
  "weights",
  "feedClasses",
  "animalAges",
  "breeds",
  "mainIngredients",
];

export const BOOLEAN_FILTER_KEYS = ["forSterilized", "hypoallergenic", "therapeuticFeed"];

const DEFAULT_PRICE_RANGE = { min: 1, max: 10000 };

export const arrayify = (value) =>
  Array.isArray(value) ? value : value != null && value !== "" ? [value] : [];

export const getProductCategories = (product = {}) =>
  normalizeProductCategories(product.category);

export const productMatchesCategory = (product, category) =>
  !category || getProductCategories(product).includes(category);

export const filterProductsByCategory = (products, category) => {
  const list = Array.isArray(products) ? products : [];
  return category
    ? list.filter((product) => productMatchesCategory(product, category))
    : list;
};

export const filterProductsBySearch = (products, query) => {
  const list = Array.isArray(products) ? products : [];
  const normalizedQuery = (query || "").trim().toLowerCase();

  if (!normalizedQuery) return list;

  return list.filter((product) =>
    (product.name || "").toLowerCase().includes(normalizedQuery)
  );
};

export const getProductFilterValues = (product = {}, key) => {
  const valueGetters = {
    categories: getProductCategories,
    kinds: (p) => arrayify(p.kind),
    types: (p) => [p.type],
    purposes: (p) => arrayify(p.purpose),
    manufacturers: (p) => [p.manufacturer],
    countries: (p) => [p.countryOfOrigin],
    weights: (p) => [p.weight],
    feedClasses: (p) => [p.feedClass],
    animalAges: (p) => arrayify(p.animalAge),
    breeds: (p) => arrayify(p.breed),
    mainIngredients: (p) => arrayify(p.mainIngredients),
  };

  return arrayify(valueGetters[key]?.(product)).filter(Boolean);
};

export const getCategoriesFromProducts = (products) => {
  const categories = new Set();
  (Array.isArray(products) ? products : []).forEach((product) => {
    getProductCategories(product).forEach((category) => {
      categories.add(category);
    });
  });
  return Array.from(categories);
};

export const normalizeFilters = (filters = {}) => {
  const triState = (value) =>
    value === true ? true : value === false ? false : null;

  return {
    categories: arrayify(filters.categories),
    kinds: arrayify(filters.kinds),
    types: arrayify(filters.types),
    purposes: arrayify(filters.purposes),
    manufacturers: arrayify(filters.manufacturers),
    countries: arrayify(filters.countries),
    weights: arrayify(filters.weights),
    priceRange:
      filters.priceRange && typeof filters.priceRange === "object"
        ? {
            min: Number(filters.priceRange.min) || DEFAULT_PRICE_RANGE.min,
            max: Number(filters.priceRange.max) || DEFAULT_PRICE_RANGE.max,
          }
        : { ...DEFAULT_PRICE_RANGE },
    feedClasses: arrayify(filters.feedClasses),
    animalAges: arrayify(filters.animalAges),
    breeds: arrayify(filters.breeds),
    mainIngredients: arrayify(filters.mainIngredients),
    forSterilized: triState(filters.forSterilized),
    hypoallergenic: triState(filters.hypoallergenic),
    therapeuticFeed: triState(filters.therapeuticFeed),
  };
};

export const pruneFiltersForProducts = (
  filters = {},
  products = [],
  animalCategory = null
) => {
  const normalized = normalizeFilters(filters);
  const scopedProducts = filterProductsByCategory(products, animalCategory);
  const nextFilters = {
    ...normalized,
    categories: animalCategory
      ? [animalCategory]
      : normalized.categories.filter((category) =>
          scopedProducts.some((product) =>
            productMatchesCategory(product, category)
          )
        ),
  };

  ARRAY_FILTER_KEYS.forEach((key) => {
    if (!normalized[key]?.length) return;

    const availableValues = new Set(
      scopedProducts.flatMap((product) => getProductFilterValues(product, key))
    );

    nextFilters[key] = normalized[key].filter((value) =>
      availableValues.has(value)
    );
  });

  BOOLEAN_FILTER_KEYS.forEach((key) => {
    if (normalized[key] == null) return;

    const hasSelectedValue = scopedProducts.some(
      (product) => product?.[key] === normalized[key]
    );

    if (!hasSelectedValue) {
      nextFilters[key] = null;
    }
  });

  return nextFilters;
};

export const extractFiltersFromProducts = (products) => {
  const kinds = new Set();
  const types = new Set();
  const purposes = new Set();
  const manufacturers = new Set();
  const countries = new Set();
  const weights = new Set();

  (Array.isArray(products) ? products : []).forEach((product) => {
    getProductFilterValues(product, "kinds").forEach((value) =>
      kinds.add(value)
    );
    getProductFilterValues(product, "types").forEach((value) =>
      types.add(value)
    );
    getProductFilterValues(product, "purposes").forEach((value) =>
      purposes.add(value)
    );
    getProductFilterValues(product, "manufacturers").forEach((value) =>
      manufacturers.add(value)
    );
    getProductFilterValues(product, "countries").forEach((value) =>
      countries.add(value)
    );
    getProductFilterValues(product, "weights").forEach((value) =>
      weights.add(value)
    );
  });

  return {
    categories: getCategoriesFromProducts(products),
    kinds: [...kinds],
    types: [...types],
    purposes: [...purposes],
    manufacturers: [...manufacturers],
    countries: [...countries],
    weights: [...weights],
  };
};
